import * as React from 'react';
import { observable, action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { TextField } from 'office-ui-fabric-react';

import { Permissions } from '@app/AppConstants';
import { ContractPartTimeSplitType, ContractType, ContractTypeModel, PersonFunctionViewModel } from '@app/Models';
import { PersonContractViewModel } from '@app/Models/WebApiModels';

import { securityService } from '@app/Services/SecurityService';

import { ContractTypeModelDropdown } from './PersonContractTypeModelComponents/ContractTypeModelDropdown';
import { ContractPartTimeMonthBlock } from './PersonContractTypeModelComponents/ContractPartTimeMonthBlock';
import { ContractPartTimeDayBlock } from './PersonContractTypeModelComponents/ContractPartTimeDayBlock';
import { ContractPartTimeVariableBlock } from './PersonContractTypeModelComponents/ContractPartTimeVariableBlock';
import { ContractPartTimeVariableDayBlock } from './PersonContractTypeModelComponents/ContractPartTimeVariableDayBlock';
import { ContractPartTimeFixBlockModelBlock } from './PersonContractTypeModelComponents/ContractPartTimeFixBlockModelBlock';
import { ContractPartTimeFixDayBlock } from './PersonContractTypeModelComponents/ContractPartTimeFixDayBlock';
import { PartTimeTzv } from '@app/AppConstants';
import { ContractPartTimeSpecialBlock } from '@app/Pages/PersonInfo/Components/PersonContractTypeModelComponents/ContractPartTimeSpecialBlock';
import { IModalDialogContent, ModalWindow, ModalDialogOptions, ModalButtonType } from '@app/Components/Modal';
import { isCabinAt } from '@app/Pages/PersonInfo/Components/Helpers/personFunctionHelper';

type ChangeContractHandler = (contract: PersonContractViewModel) => Promise<boolean>;

type PartTimeTzvBlockProps = {
    contract: PersonContractViewModel;
    personFunctions: PersonFunctionViewModel[];
    onSave: ChangeContractHandler;
};

@observer
export class PartTimeTzvBlock extends React.Component<PartTimeTzvBlockProps, {}> implements IModalDialogContent<void> {
    private readonly _store: PartTimeTzvBlockStore;

    constructor(props: PartTimeTzvBlockProps) {
        super(props);
        this._store = new PartTimeTzvBlockStore(props);
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        const { hasPermission } = securityService;

        return {
            title: 'Part Time TZV Model',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }, {
                type: ModalButtonType.Save,
                isDisabled: !this._store.isValidForm || (!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)),
                onClick: async () => {
                    if (await this._store.onSave()) {
                        window.close(ModalButtonType.Save);
                    }
                }
            }],
            width: '350px'
        };
    }

    render() {
        const { hasPermission, permissionCss } = securityService;
        return (
            <>
                <ContractTypeModelDropdown
                    type={this._store.contractType}
                    isCabin={this._store.isCabin}
                    isCombinedContractTypeModel={this._store.isCombinedContractTypeModel}
                    typeModel={this._store.typeModel}
                    onChange={this._store.onChangeTypeModel}
                    className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                    disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                />
                {this._store.typeModel === ContractTypeModel.TZV_Month &&
                    <ContractPartTimeMonthBlock
                        value={this._store.partTimeMonthContractDefinition}
                        onChange={this._store.onChangePartTimeMonthContractDefinition}
                        className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                        disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                    />
                }
                {this._store.typeModel === ContractTypeModel.TZV_Day &&
                    <ContractPartTimeDayBlock
                        onChangeContractDefinition={this._store.onChangePartTimeDayContractDefinition}
                        onChangeSplitType={this._store.onChangePartTimeDaySplitType}
                        contractDefinition={this._store.partTimeDayContractDefinition}
                        splitType={this._store.partTimeDaySplitType}
                        className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                        disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                    />
                }
                {this._store.typeModel === ContractTypeModel.TZV_VariableBlock &&
                    <ContractPartTimeVariableBlock
                        onChangeBlockLength={this._store.onChangePartTimeVariableBlockLength}
                        onChangeBlockPerYear={this._store.onChangePartTimeVariableBlockPerYear}
                        blockLength={this._store.partTimeVariableBlockLength}
                        blockPerYear={this._store.partTimeVariableBlockPerYear}
                        className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                        disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                    />
                }
                {(this._store.typeModel === ContractTypeModel.TZV_VariableDay || this._store.typeModel === ContractTypeModel.TZV_VariableDay_LhSff) &&
                    <ContractPartTimeVariableDayBlock
                        onChangeBlockLength={this._store.onChangePartTimeVariableDayBlockLength}
                        onChangeSplitType={this._store.onChangePartTimeVariableDaySplitType}
                        blockLength={this._store.partTimeVariableDayBlockLength}
                        blockSplitType={this._store.partTimeVariableDaySplitType}
                        className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                        disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                        typeModel={this._store.typeModel}
                    />
                }
                {this._store.typeModel === ContractTypeModel.TZV_FixBlock &&
                    <>
                        <ContractPartTimeFixBlockModelBlock
                            onChangeBlockDays={this._store.onChangePartTimeFixBlockDays}
                            onChangeBlockFrequency={this._store.onChangePartTimeFixBlockFrequency}
                            onChangeBlockBeginDay={this._store.onChangePartTimeFixBlockBeginDay}
                            onChangeBlockBeginMonth={this._store.onChangePartTimeFixBlockBeginMonth}
                            blockDays={this._store.partTimeFixBlockDays}
                            blockFrequency={this._store.partTimeFixBlockFrequency}
                            blockBeginDay={this._store.partTimeFixBlockBeginDay}
                            blockBeginMonth={this._store.partTimeFixBlockBeginMonth}
                            className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                            disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                        />
                        {this._store.partTimeFixBlockDays && this._store.partTimeFixBlockFrequency && !this._store.isValidOffLengthFixBlock &&
                            <span className="ms-TextField-errorMessage errorMessage">{this._store.offLengthFixBlockErrorMessage}</span>
                        }
                    </>
                }
                {(this._store.typeModel === ContractTypeModel.TZV_FixDay || this._store.typeModel === ContractTypeModel.TZV_FixDay_Sh || this._store.typeModel === ContractTypeModel.TZV_FixDay_Mff) &&
                    <ContractPartTimeFixDayBlock
                        onChangeContractDefinition={this._store.onChangePartTimeFixDayContractDefinition}
                        onChangeSplitType={this._store.onChangePartTimeFixDaySplitType}
                        onChangeStartWeekPerContract={this._store.onChangePartTimeFixDaysStartWeekPerContract}
                        onChangeDaysPerWeek={this._store.onChangePartTimeFixDaysPerWeek}
                        onChangeFirstDay={this._store.onChangePartTimeFixDayFirstDay}
                        onChangeSecondDay={this._store.onChangePartTimeFixDaySecondDay}
                        onChangeReason={this._store.onChangePartTimeFixDayReason}
                        contractDefinition={this._store.partTimeFixDayContractDefinition}
                        splitType={this._store.partTimeFixDaySplitType}
                        startWeekPerContract={this._store.partTimeFixDaysStartWeekPerContract}
                        daysPerWeek={this._store.partTimeFixDaysPerWeek}
                        firstDay={this._store.partTimeFixDayFirstDay}
                        secondDay={this._store.partTimeFixDaySecondDay}
                        reason={this._store.partTimeFixDayReason}
                        className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                        disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                        isCabin={this._store.isCabin}
                        typeModel={this._store.typeModel}
                    />
                }
                {this._store.typeModel === ContractTypeModel.TZV_Spez &&
                    <ContractPartTimeSpecialBlock
                        onChangeDaysCount={this._store.onChangePartTimeSpecialDaysCount}
                        onChangeVacEntitlement={this._store.onChangePartTimeSpecialVacEntitlement}
                        daysCount={this._store.partTimeSpecialDaysPerYear}
                        vacEntitlement={this._store.partTimeSpecialVacEntitlement}
                        className={permissionCss(Permissions.ViewObjectPersonContractTZVPart, Permissions.EditObjectPersonContractTZVPart)}
                        disabled={!hasPermission(Permissions.EditObjectPersonContractTZVPart, true)}
                    />
                }
            </>
        );
    }

    private getErrorMessageForEmployment = (value: string): string => {
        if (value && (parseFloat(value) > 100)) {
            return 'Max value is 100%';
        }
        return '';
    };

}

class PartTimeTzvBlockStore {
    @observable typeModel?: ContractTypeModel;
    @observable contract: PersonContractViewModel;
    @observable partTimeMonthContractDefinition?: number;
    @observable partTimeDayContractDefinition?: number;
    @observable partTimeDaySplitType?: ContractPartTimeSplitType;
    @observable partTimeVariableBlockLength?: number;
    @observable partTimeVariableBlockPerYear?: number;
    @observable partTimeVariableDayBlockLength?: number;
    @observable partTimeVariableDaySplitType?: ContractPartTimeSplitType;
    @observable partTimeFixBlockDays?: number;
    @observable partTimeFixBlockFrequency?: number;
    @observable partTimeFixBlockBeginDay?: number;
    @observable partTimeFixBlockBeginMonth?: number;
    @observable partTimeFixDaysStartWeekPerContract?: number;
    @observable partTimeFixDaysPerWeek?: number;
    @observable partTimeFixDayContractDefinition?: number;
    @observable partTimeFixDayFirstDay?: number;
    @observable partTimeFixDaySecondDay?: number;
    @observable partTimeFixDaySplitType?: ContractPartTimeSplitType;
    @observable partTimeFixDayReason?: string;
    @observable partTimeSpecialDaysPerYear?: number;
    @observable partTimeSpecialVacEntitlement?: number;
    @observable isCabin: boolean = false;

    private readonly _currentProps: PartTimeTzvBlockProps;

    constructor(props: PartTimeTzvBlockProps) {
        this._currentProps = props;
        this.isCabin = isCabinAt(this._currentProps.personFunctions, this._currentProps.contract.dateFrom);
        this.updateProps();
    }

    updateProps() {
        this.updateAfterReceiveProps(this._currentProps);
        if (!this.typeModel || !this.isTzvTypeModel(this.typeModel)) {
            this.typeModel = ContractTypeModel.TZV_Ground;
        }
    }

    @action.bound
    async onSave() {
        if (this._currentProps.onSave) {
            const newContract = this.newContract();
            const result = await this._currentProps.onSave(newContract as PersonContractViewModel);
            if (result) {
                this.contract = newContract;
            }
            return result;
        }
        return false;
    }

    newContract() {
        const contract = { ...this.contract };
        contract.typeModel = this.typeModel as keyof ContractTypeModel;

        if (this.typeModel !== ContractTypeModel.TZV_Month) {
            contract.partTimeMonthContractDefinition = undefined;
        } else {
            contract.partTimeMonthContractDefinition = this.partTimeMonthContractDefinition;
        }
        if (this.typeModel !== ContractTypeModel.TZV_Day) {
            contract.partTimeDayContractDefinition = undefined;
            contract.partTimeDaySplitType = undefined;
        } else {
            contract.partTimeDayContractDefinition = this.partTimeDayContractDefinition;
            contract.partTimeDaySplitType = this.partTimeDaySplitType;
        }
        if (this.typeModel !== ContractTypeModel.TZV_VariableBlock) {
            contract.partTimeVariableBlockLength = undefined;
            contract.partTimeVariableBlockPerYear = undefined;
        } else {
            contract.partTimeVariableBlockLength = this.partTimeVariableBlockLength;
            contract.partTimeVariableBlockPerYear = this.partTimeVariableBlockPerYear;
        }
        if (this.typeModel !== ContractTypeModel.TZV_VariableDay && this.typeModel !== ContractTypeModel.TZV_VariableDay_LhSff) {
            contract.partTimeVariableDayBlockLength = undefined;
            contract.partTimeVariableDaySplitType = undefined;
        } else {
            contract.partTimeVariableDayBlockLength = this.partTimeVariableDayBlockLength;
            contract.partTimeVariableDaySplitType = this.partTimeVariableDaySplitType;
        }
        if (this.typeModel !== ContractTypeModel.TZV_FixDay && this.typeModel !== ContractTypeModel.TZV_FixDay_Sh && this.typeModel !== ContractTypeModel.TZV_FixDay_Mff) {
            if (this.typeModel !== ContractTypeModel.TZV_Day) {
                contract.partTimeDayContractDefinition = undefined;
                contract.partTimeDaySplitType = undefined;
            }
            contract.partTimeFixDaysPerWeek = undefined;
            contract.partTimeFixDayFirstDay = undefined;
            contract.partTimeFixDaySecondDay = undefined;
            contract.partTimeFixDayReason = undefined;
            if (this.typeModel !== ContractTypeModel.TZV_FixDay_Mff) {
                contract.partTimeFixDaysStartWeekPerContract = undefined;
            }
        } else {
            if (this.isCabin) {
                contract.partTimeDayContractDefinition = this.partTimeFixDayContractDefinition;
                contract.partTimeDaySplitType = this.partTimeFixDaySplitType;
            } else {
                contract.partTimeDayContractDefinition = undefined;
                contract.partTimeDaySplitType = undefined;
            }
            contract.partTimeFixDaysPerWeek = this.partTimeFixDaysPerWeek;
            contract.partTimeFixDayFirstDay = this.partTimeFixDayFirstDay;
            contract.partTimeFixDaySecondDay = this.partTimeFixDaysPerWeek === 2 ? this.partTimeFixDaySecondDay : undefined;
            contract.partTimeFixDayReason = this.partTimeFixDayReason;
            if (this.typeModel === ContractTypeModel.TZV_FixDay_Mff) {
                contract.partTimeFixDaysStartWeekPerContract = this.partTimeFixDaysStartWeekPerContract;
            }
        }
        if (this.typeModel !== ContractTypeModel.TZV_FixBlock) {
            contract.partTimeFixBlockBeginDay = undefined;
            contract.partTimeFixBlockBeginMonth = undefined;
            contract.partTimeFixBlockDays = undefined;
            contract.partTimeFixBlockFrequency = undefined;
        } else {
            contract.partTimeFixBlockBeginDay = this.partTimeFixBlockBeginDay;
            contract.partTimeFixBlockBeginMonth = this.partTimeFixBlockBeginMonth;
            contract.partTimeFixBlockDays = this.partTimeFixBlockDays;
            contract.partTimeFixBlockFrequency = this.partTimeFixBlockFrequency;
        }
        if (this.typeModel !== ContractTypeModel.TZV_Spez) {
            contract.partTimeSpecialDaysPerYear = undefined;
            this.partTimeSpecialVacEntitlement = undefined;
        } else {
            contract.partTimeSpecialDaysPerYear = this.partTimeSpecialDaysPerYear;
            contract.partTimeSpecialVacEntitlement = this.partTimeSpecialVacEntitlement;
        }
        return contract;
    }

    @computed
    get isValidForm() {
        return !!((this.typeModel === ContractTypeModel.TZV_Month && this.partTimeMonthContractDefinition) ||
            (this.typeModel === ContractTypeModel.TZV_Day && this.partTimeDayContractDefinition && this.partTimeDaySplitType) ||
            (this.typeModel === ContractTypeModel.TZV_VariableBlock && this.partTimeVariableBlockLength && this.partTimeVariableBlockPerYear) ||
            ((this.typeModel === ContractTypeModel.TZV_VariableDay || this.typeModel === ContractTypeModel.TZV_VariableDay_LhSff) && this.partTimeVariableDayBlockLength && this.partTimeVariableDaySplitType) ||
            (this.typeModel === ContractTypeModel.TZV_FixBlock && this.partTimeFixBlockDays && this.partTimeFixBlockFrequency && this.partTimeFixBlockBeginDay && this.partTimeFixBlockBeginMonth && this.isValidOffLengthFixBlock) ||
            ((this.typeModel === ContractTypeModel.TZV_FixDay || this.typeModel === ContractTypeModel.TZV_FixDay_Sh) && this.partTimeFixDaysPerWeek && this.partTimeFixDayFirstDay && (!this.partTimeFixDaySecondDay || this.partTimeFixDaySecondDay !== this.partTimeFixDayFirstDay) &&
                !this.isCabin || (this.partTimeFixDayContractDefinition)
            ) ||
            (this.typeModel === ContractTypeModel.TZV_FixDay_Mff && this.partTimeFixDaysStartWeekPerContract && this.partTimeFixDaysPerWeek && this.partTimeFixDayFirstDay && (!this.partTimeFixDaySecondDay || this.partTimeFixDaySecondDay !== this.partTimeFixDayFirstDay) && !this.isCabin) ||
            (this.typeModel === ContractTypeModel.TZV_Spez && this.isValidDaysCountSpecialBlock && this.isValidVacEntitlementSpecialBlock) ||
            this.typeModel === ContractTypeModel.TZV_Ground ||
            this.typeModel === ContractTypeModel.TZV_R
        );
    }

    @action.bound
    onChangeTypeModel(newValue: ContractTypeModel) {
        this.typeModel = newValue;
    }

    @action.bound
    onChangePartTimeMonthContractDefinition(newValue: number) {
        this.partTimeMonthContractDefinition = newValue;
    }

    @action.bound
    onChangePartTimeDayContractDefinition(newValue: number) {
        this.partTimeDayContractDefinition = newValue;
    }

    @action.bound
    onChangePartTimeDaySplitType(newValue: ContractPartTimeSplitType) {
        this.partTimeDaySplitType = newValue;
    }

    @action.bound
    onChangePartTimeVariableBlockLength(newValue: number) {
        this.partTimeVariableBlockLength = newValue;
    }

    @action.bound
    onChangePartTimeVariableBlockPerYear(newValue?: number) {
        this.partTimeVariableBlockPerYear = newValue;
    }

    @action.bound
    onChangePartTimeVariableDayBlockLength(newValue: number) {
        this.partTimeVariableDayBlockLength = newValue;
    }

    @action.bound
    onChangePartTimeVariableDaySplitType(newValue: ContractPartTimeSplitType) {
        this.partTimeVariableDaySplitType = newValue;
    }

    @action.bound
    onChangePartTimeFixBlockDays(newValue: number) {
        this.partTimeFixBlockDays = newValue;
    }

    @action.bound
    onChangePartTimeFixBlockFrequency(newValue: number) {
        this.partTimeFixBlockFrequency = newValue;
    }

    @action.bound
    onChangePartTimeFixBlockBeginDay(newValue: number) {
        this.partTimeFixBlockBeginDay = newValue;
    }

    @action.bound
    onChangePartTimeFixBlockBeginMonth(newValue: number) {
        this.partTimeFixBlockBeginMonth = newValue;
    }

    @action.bound
    onChangePartTimeFixDaysStartWeekPerContract(newValue: number) {
        this.partTimeFixDaysStartWeekPerContract = newValue;
    }

    @action.bound
    onChangePartTimeFixDaysPerWeek(newValue: number) {
        this.partTimeFixDaysPerWeek = newValue;
        if (newValue === 1) {
            this.partTimeFixDaySecondDay = undefined;
        }
    }

    @action.bound
    onChangePartTimeFixDayContractDefinition(newValue: number) {
        this.partTimeFixDayContractDefinition = newValue;
    }

    @action.bound
    onChangePartTimeFixDayFirstDay(newValue: number) {
        this.partTimeFixDayFirstDay = newValue;
    }

    @action.bound
    onChangePartTimeFixDaySecondDay(newValue: number) {
        this.partTimeFixDaySecondDay = newValue;
    }

    @action.bound
    onChangePartTimeFixDaySplitType(newValue?: ContractPartTimeSplitType) {
        this.partTimeFixDaySplitType = newValue;
    }

    @action.bound
    onChangePartTimeFixDayReason(newValue?: string) {
        this.partTimeFixDayReason = newValue;
    }

    @action.bound
    onChangePartTimeSpecialDaysCount(newValue?: number) {
        this.partTimeSpecialDaysPerYear = newValue;
    }

    @action.bound
    onChangePartTimeSpecialVacEntitlement(newValue?: number) {
        this.partTimeSpecialVacEntitlement = newValue;
    }

    @computed
    get contractType() {
        return this.contract.type as ContractType;
    }

    @computed
    get isCombinedContractTypeModel() {
        return this.contract.type === ContractType.Tzv && !!this.contract.combinationVariableDayBlockLength && !!this.contract.combinationVariableDaySplitType;
    }

    @computed
    get isValidOffLengthFixBlock() {
        return this.partTimeFixBlockDays && this.partTimeFixBlockFrequency && ((this.partTimeFixBlockDays * this.partTimeFixBlockFrequency) >= PartTimeTzv.minOffLength) && ((this.partTimeFixBlockDays * this.partTimeFixBlockFrequency) <= PartTimeTzv.maxOffLength);
    }

    @computed
    get isValidVacEntitlementSpecialBlock() {
        return this.partTimeSpecialVacEntitlement && this.partTimeSpecialVacEntitlement >= 0 && this.partTimeSpecialVacEntitlement <= 1;
    }

    @computed
    get isValidDaysCountSpecialBlock() {
        return this.partTimeSpecialDaysPerYear && this.partTimeSpecialDaysPerYear >= 1 && this.partTimeSpecialDaysPerYear <= 365;
    }

    @computed
    get offLengthFixBlockErrorMessage() {
        return this.partTimeFixBlockDays && this.partTimeFixBlockFrequency && `The calculated OFF-Length ( BlockLength * BlockFrequencyPerYear = ${this.partTimeFixBlockDays} * ${this.partTimeFixBlockFrequency} = ${this.partTimeFixBlockDays * this.partTimeFixBlockFrequency}) is not withing min/max range of ${PartTimeTzv.minOffLength}/${PartTimeTzv.maxOffLength}`;
    }

    @action
    updateAfterReceiveProps(props: PartTimeTzvBlockProps) {
        let contract = props.contract;
        this.contract = contract;
        this.typeModel = contract.typeModel;
        this.partTimeMonthContractDefinition = contract.partTimeMonthContractDefinition;
        this.partTimeDayContractDefinition = contract.partTimeDayContractDefinition;
        this.partTimeDaySplitType = contract.partTimeDaySplitType;
        this.partTimeVariableBlockLength = contract.partTimeVariableBlockLength;
        this.partTimeVariableBlockPerYear = contract.partTimeVariableBlockPerYear;
        this.partTimeVariableDayBlockLength = contract.partTimeVariableDayBlockLength;
        this.partTimeVariableDaySplitType = contract.partTimeVariableDaySplitType;
        this.partTimeFixBlockDays = contract.partTimeFixBlockDays;
        this.partTimeFixBlockFrequency = contract.partTimeFixBlockFrequency;
        this.partTimeFixBlockBeginDay = contract.partTimeFixBlockBeginDay;
        this.partTimeFixBlockBeginMonth = contract.partTimeFixBlockBeginMonth;
        this.partTimeFixDaysPerWeek = contract.partTimeFixDaysPerWeek || 1;
        this.partTimeFixDayFirstDay = contract.partTimeFixDayFirstDay;
        this.partTimeFixDaySecondDay = contract.partTimeFixDaySecondDay;
        this.partTimeFixDayReason = contract.partTimeFixDayReason;
        this.partTimeSpecialDaysPerYear = contract.partTimeSpecialDaysPerYear;
        this.partTimeSpecialVacEntitlement = contract.partTimeSpecialVacEntitlement;
        if (this.isCabin) {
            this.partTimeFixDayContractDefinition = contract.partTimeDayContractDefinition;
            this.partTimeFixDaySplitType = contract.partTimeDaySplitType;
        }
        if (!this.isCabin) {
            this.partTimeFixDaysStartWeekPerContract = contract.partTimeFixDaysStartWeekPerContract;
        }
    }

    isTzvTypeModel(contractType: ContractTypeModel) {
        return contractType === ContractTypeModel.TZV_Ground || contractType === ContractTypeModel.TZV_FixDay ||
            contractType === ContractTypeModel.TZV_FixBlock || contractType === ContractTypeModel.TZV_VariableDay ||
            contractType === ContractTypeModel.TZV_VariableBlock || contractType === ContractTypeModel.TZV_Day ||
            contractType === ContractTypeModel.TZV_FixDay_Sh || contractType === ContractTypeModel.TZV_FixDay_Mff ||
            contractType === ContractTypeModel.TZV_VariableDay_LhSff || contractType === ContractTypeModel.TZV_R ||
            contractType === ContractTypeModel.TZV_Month || contractType === ContractTypeModel.TZV_Spez;
    }
}